import { XI_SIZE, bench as benchOf, moveUp, moveDown, moveBowler } from "./pickXiSheet";

/**
 * Issue #745 (Pick XI coach, On the day; D297, D302, D316 Q3/Q7) — the state
 * model for On the day. Pure functions: no React, no network. A refused move
 * (no reason, someone already in the side) returns the state it was given.
 *
 * Shape, the one `pickXiSheet` uses plus what a swap records:
 *   {
 *     squad:   [{ player_id, full_name }]
 *     names:   { [id]: name }                         squad and sheets, so a name survives a squad edit
 *     tags:    { [id]: { captain, vice, keeper } }    the PUBLISHED tags: a player keeps them, one coming in from nowhere has none
 *     xi:      [{ id, captain, vice, keeper, replaces, reason }]   batting order; index + 1 is the place
 *     twelfth: id | null
 *     bowling: [id]
 *   }
 *
 * `replaces` is the PUBLISHED player whose seat this is, and `reason` is
 * `unwell` or `injured`: what the API wants (`PUT /day` refuses a swap that
 * names anyone else, #1314). Tags are not edited on the day and are not sent;
 * the API gives each player their published tags and a player coming in none.
 *
 * THE INVARIANT (review F1, #1339), kept by `swapOut` and checked by
 * `pickXiDay.chains.test.js` against the API's rules: a published player in
 * today's XI carries no record (except in a sheet saved by an earlier build, with
 * nobody standing in for him: then he carries it himself, which the API also
 * accepts); every player in it who was NOT in the published
 * XI carries one, naming a different published player who is out of it, once
 * each. The records are the pairing of "in" with "out".
 */

export { moveUp, moveDown, moveBowler };

/** The two reasons, in the order the board draws them. One of two, so it can be read back (#745). */
export const SWAP_REASONS = [
  { value: "unwell", label: "Unwell", testId: "xi-reason-unwell" },
  { value: "injured", label: "Injured", testId: "xi-reason-injured" },
];

const REASON_VALUES = SWAP_REASONS.map((r) => r.value);
const NO_TAGS = { captain: false, vice: false, keeper: false };

const inSquad = (state, id) => state.squad.some((m) => m.player_id === id);
const inXi = (state, id) => state.xi.some((e) => e.id === id);

/**
 * The state a `GET /api/coach-games/:id` payload opens as: the day sheet when
 * one was saved, otherwise the published team. A player the sheet names who is
 * no longer in the squad is dropped, because `PUT /day` refuses one
 * (`not_in_squad`) and the coach would be unable to save.
 */
export function fromGame(game) {
  const squad = (game?.squad || []).map((m) => ({ player_id: m.player_id, full_name: m.full_name }));
  const published = game?.sheets?.published || null;
  const source = game?.sheets?.day || published;

  const names = {};
  for (const e of published?.xi || []) names[e.player_id] = e.full_name;
  if (published?.twelfth) names[published.twelfth.player_id] = published.twelfth.full_name;
  for (const e of source?.xi || []) names[e.player_id] = names[e.player_id] || e.full_name;
  for (const m of squad) names[m.player_id] = m.full_name;

  const tags = {};
  for (const e of published?.xi || []) tags[e.player_id] = { captain: !!e.captain, vice: !!e.vice_captain, keeper: !!e.keeper };

  const base = { squad, names, tags, xi: [], twelfth: null, bowling: [] };
  if (!source) return base;

  const ids = new Set(squad.map((m) => m.player_id));
  const entries = [...(source.xi || [])]
    .filter((e) => ids.has(e.player_id))
    .sort((a, b) => (a.bat_order ?? 99) - (b.bat_order ?? 99))
    .slice(0, XI_SIZE);
  // A record is kept only when `PUT /day` would accept it: it names a published player who is out of
  // today's XI, once each, with one of the two reasons. Anything else is read as no record.
  const inDay = new Set(entries.map((e) => e.player_id));
  const named = new Set();
  const xi = entries.map((e) => {
    const r = e.replaces_player_id;
    const ok = !!r && r !== e.player_id && !!tags[r] && !inDay.has(r) && !named.has(r) && REASON_VALUES.includes(e.swap_reason);
    if (ok) named.add(r);
    return { id: e.player_id, captain: !!e.captain, vice: !!e.vice_captain, keeper: !!e.keeper, replaces: ok ? r : null, reason: ok ? e.swap_reason : null };
  });
  const bowling = entries
    .filter((e) => e.bowl_order != null)
    .sort((a, b) => a.bowl_order - b.bowl_order)
    .map((e) => e.player_id);
  const twelfth = source.twelfth && ids.has(source.twelfth.player_id) ? source.twelfth.player_id : null;
  return { ...base, xi, twelfth, bowling };
}

/** The squad players who are neither in today's XI nor the 12th man, in squad order. */
export const bench = benchOf;

/** The name to print for a player id. */
export const nameOf = (state, id) => state.names[id] || "";

/**
 * Take the batter at place `n` (1-based) out and put `inId` in their place, for
 * `reason`. The incoming player must be the 12th man or on the bench. The
 * outgoing player leaves the bowling order; the incoming one is not added
 * (the coach orders the bowling after). The 12th slot empties when the 12th
 * man comes in.
 *
 * The record names the PUBLISHED player whose seat it is, so swapping out
 * someone who came in earlier reads "in for the original", not "in for the
 * substitute" (the API knows only the published team).
 *
 * When a PUBLISHED player comes back in, he carries no record, and the
 * substitute who stood in for him takes over the record this swap makes (review
 * F1, #1339): the pair "in" and "out" stays whole, so no seat is left without
 * one and a later swap of that substitute names the right player. If the
 * substitute is the one being swapped out, the swap is simply undone.
 */
export function swapOut(state, n, inId, reason) {
  const out = state.xi[n - 1];
  if (!out || !REASON_VALUES.includes(reason)) return state;
  if (!inSquad(state, inId) || inXi(state, inId)) return state;

  const published = (id) => Object.prototype.hasOwnProperty.call(state.tags, id);
  // The published player whose seat this is: the record, or the outgoing player themselves if they are one.
  const origin = out.replaces || (published(out.id) ? out.id : null);
  const own = { id: inId, ...(state.tags[inId] || NO_TAGS) };
  let xi = state.xi.map((e, i) => (i === n - 1 ? { ...own, replaces: null, reason: null } : e));

  if (!published(inId)) {
    xi[n - 1] = { ...own, replaces: origin, reason: origin ? reason : null };
  } else if (origin !== inId) {
    // A published player is back. Whoever stood in for him takes over the record; with nobody to
    // take it, he carries it himself (it names a published player who is out, which the API accepts).
    const z = xi.findIndex((e, i) => i !== n - 1 && e.replaces === inId);
    if (z >= 0) xi[z] = { ...xi[z], replaces: origin, reason: origin ? reason : null };
    else xi[n - 1] = { ...own, replaces: origin, reason: origin ? reason : null };
  }
  // No other record names the incoming player: they are in the XI themselves.
  xi = xi.map((e, i) => (i !== n - 1 && e.replaces === inId ? { ...e, replaces: null, reason: null } : e));
  return {
    ...state,
    xi,
    twelfth: state.twelfth === inId ? null : state.twelfth,
    bowling: state.bowling.filter((id) => id !== out.id),
  };
}

/** The 12th man out and a bench player in as the 12th man. No reason: the API stores none for the 12th seat. */
export function swapTwelfth(state, inId) {
  if (!inSquad(state, inId) || inXi(state, inId) || state.twelfth === inId) return state;
  return { ...state, twelfth: inId };
}

/**
 * Who can come in, in the order the swap sheet lists them: the 12th man first
 * (when still unused, and when it is not the 12th man being swapped out), then
 * the bench in squad order. `role` is the prefix the board prints on the line.
 */
export function incoming(state, { forTwelfth }) {
  const list = [];
  if (state.twelfth && !forTwelfth) list.push({ id: state.twelfth, name: nameOf(state, state.twelfth), role: "twelfth" });
  for (const m of benchOf(state)) list.push({ id: m.player_id, name: m.full_name, role: "bench" });
  return list;
}

/** The swaps made, in batting order: `{ id, name, outName, reason }` each. */
export function changes(state) {
  return state.xi
    .filter((e) => e.replaces && e.reason && Object.prototype.hasOwnProperty.call(state.tags, e.replaces))
    .map((e) => ({ id: e.id, name: nameOf(state, e.id), outId: e.replaces, outName: nameOf(state, e.replaces), reason: e.reason }));
}

/**
 * The summary line: "1 change today: [IN] in for [OUT], unwell." as the board
 * draws it. For two or more the clauses join with "; " (the planner's minimal
 * extension, S3). Null with no changes.
 */
export function summaryLine(state) {
  const list = changes(state);
  if (list.length === 0) return null;
  const clauses = list.map((c) => `${c.name} in for ${c.outName}, ${c.reason}`);
  return `${list.length} ${list.length === 1 ? "change" : "changes"} today: ${clauses.join("; ")}.`;
}

/** The `PUT /api/coach-games/:id/day` body. Tags are not sent: the API keeps each player's published ones. */
export function toBody(state) {
  return {
    xi: state.xi.map((e, i) => ({ player_id: e.id, bat_order: i + 1, replaces_player_id: e.replaces, swap_reason: e.reason })),
    twelfth: state.twelfth,
    bowling: [...state.bowling],
  };
}

/**
 * The team as published, read only, for Planned team: the XI in batting order
 * with its tags, and the 12th man. Null when nothing was published. Never
 * changed by a swap: it reads `sheets.published` and nothing else.
 */
export function planned(game) {
  const published = game?.sheets?.published;
  if (!published) return null;
  const xi = [...(published.xi || [])].sort((a, b) => (a.bat_order ?? 99) - (b.bat_order ?? 99));
  return {
    rows: xi.map((e, i) => ({ n: i + 1, id: e.player_id, name: e.full_name, captain: !!e.captain, vice: !!e.vice_captain, keeper: !!e.keeper })),
    twelfth: published.twelfth ? { id: published.twelfth.player_id, name: published.twelfth.full_name } : null,
  };
}
